import { combineActions, handleActions } from 'redux-actions'
import { fromJS, List, Map, Record } from 'immutable'
import { FLUSH, REHYDRATE } from 'redux-persist'
import moment from 'moment'

import { ActiveProTrackLevel } from 'store/records/proTracks'
import {
  ActivitiesHistoryItem,
  ActivitiesHistoryPagination,
  AuthToken,
  FeaturesNotification,
  Identity,
  IdentityOrganization,
  IdentityPending,
  NotificationTypes,
  OnboardingChecklist,
  OnboardingLevel,
  OnboardingLevels,
  OnboardingProTrack,
  Progress,
  ProgressAllInstance,
  ProgressCurrentInstance,
  Sprint,
  SprintGoalRecord,
} from 'store/entities/identity/records'
import { Activity } from 'store/records/activity'
import {
  activityActions,
  authLogin,
  changeIdentityRole,
  clearAuthStatuses,
  getActivitiesHistory,
  getIdentityCombinedProgress,
  getIdentityProgress,
  getProfile,
  identity,
  markEndSurveyRequested,
  markLoginFlowAsFinished,
  postAuthEmail,
  postAuthPhone,
  requestSurvey,
  saveProfile,
  sendYesterdayActivity,
  setAuthEmailStatus,
  setCustomStory,
  setFeatureAsShown,
  setFeatureTooltipAsShown,
  setFirstLogin,
  setIsUserEverTakeProTrack,
  setOneSignalId,
  setYesterdayStory,
  silentSubscribeToProTrack,
  subscribeToProTrack,
  unsubscribeProgress,
  updateActivity,
  updateOnboardingChecklist,
  updateSubscribeToProTrack,
} from 'store/entities/identity/actions'
import { DID } from 'app/constants/identityActions'
import { setStory } from 'store/entities/activity/actions'
import * as types from 'store/entities/identity/actionTypes'
import { FEED_SET_REACTION } from 'store/entities/feed/actionTypes'
import { deleteStoryApiRequest } from 'store/entities/feed/actions'
import { IActivitiesHistoryItem } from './interface'

const handleSetAuthEmailStatus = (state: any, { payload }: any) => {
  return state.set('authEmailStatus', payload.status)
}

const handleRehydrate = (state: any, { payload }: any) => {
  return payload && payload.identity && payload.identity.set
    ? payload.identity.set('pending', new IdentityPending())
    : state
}

const handleFlush = () => {
  return new Identity()
}

export const handleAuthLoginSuccess = (state: any, { payload }: any) => {
  const { access_token, first_login, id, roles } = payload
  const preparedRoles = List(roles.map((role: any) => Map({ name: role })))
  const userRoleIndex = preparedRoles.findIndex((role: any) => role.get('name') === 'user')
  return state
    .set('id', id)
    .set('firstLogin', first_login)
    .set('roles', preparedRoles)
    .set('activeRole', userRoleIndex)
    .set('authToken', new AuthToken({ accessToken: access_token }))
}

const handleChangeIdentityRole = (state: any, { payload: { roleIndex } }: any) => {
  return state.set('activeRole', roleIndex)
}

const getProfileRequest = (state: any) => {
  if (!state.get('email')) {
    return state.setIn(['beforeLoading', 'profile'], true).setIn(['pending', 'getProfile'], true)
  }
  return state.setIn(['beforeLoading', 'profile'], true)
}

const getProfileSuccess = (state: any, { payload }: any) => {
  const {
    roles,
    firstName,
    lastName,
    avatar,
    email,
    phone,
    phoneISO,
    commitNotificationTime,
    commitedNotificationTime,
    notificationType,
    timezone,
    dayoffs,
    organizationId,
    workWeek,
    isWorkWeekTouched,
    oneSignals,
    oneSignalAppId,
    isShowFeatureInfo,
    teams,
    isLoginFlowFinished,
    badges,
    isWorkingDay,
    onboardingChecklist,
    isUserHaveOnlyFirstActivity,
    isUserEverTakeProTrack,
  } = payload.data ? payload.data : payload // TODO: remove after API implementation approve
  if (isShowFeatureInfo && isShowFeatureInfo.sms) {
    isShowFeatureInfo.smsTooltip = true
  }

  const userSprint = state.getIn(['sprint', 'user'])
  if (badges.activeBadge) {
    badges.activeBadge.isNotAchieved = userSprint.achievedPoints < userSprint.goal
  }
  const newIsUserEverTakeProTrack = state.get('isUserEverTakeProTrack') || isUserEverTakeProTrack

  moment.tz.setDefault(timezone)
  return state
    .set('roles', fromJS(roles))
    .set('avatar', avatar)
    .set('firstName', firstName)
    .set('lastName', lastName)
    .set('email', email)
    .set('phone', phone)
    .set('phoneISO', phoneISO)
    .set('commitNotificationTime', commitNotificationTime)
    .set('committedNotificationTime', commitedNotificationTime)
    .set('notificationType', new NotificationTypes(notificationType))
    .set('timezone', timezone)
    .set('dayoffs', fromJS(dayoffs))
    .set('organizationId', organizationId)
    .set('workWeek', List(workWeek))
    .set('isWorkWeekTouched', isWorkWeekTouched)
    .set('oneSignals', List(oneSignals))
    .set('oneSignalAppId', oneSignalAppId)
    .set('isShowFeatureInfo', new FeaturesNotification(isShowFeatureInfo))
    .setIn(['pending', 'getProfile'], false)
    .set('teams', List(teams))
    .set('isLoginFlowFinished', isLoginFlowFinished)
    .set('onboardingChecklist', new OnboardingChecklist(onboardingChecklist))
    .set('isUserHaveOnlyFirstActivity', isUserHaveOnlyFirstActivity)
    .set('badges', badges)
    .set('isWorkingDay', isWorkingDay)
    .set('isUserEverTakeProTrack', newIsUserEverTakeProTrack)
}

const getProfileFail = (state: any) => {
  return state.setIn(['pending', 'getProfile'], false)
}

const logoutSuccess = (state: any) => {
  const identity = new Identity()
  return identity.set('activeRole', state.get('activeRole')).set('stage', state.get('stage'))
}

const activityActionsCommitSuccess = (state: any, { payload }: any) => {
  const parsedActions =
    payload.data &&
    payload.data.actions.reduce((acc: any, action: string) => {
      acc[action] = action
      return acc
    }, {})
  return state.set('actions', fromJS(parsedActions))
}

const activityActionDidSuccess = (state: any) => {
  return state.setIn(['onboardingChecklist', 'isFirstActionCompleted'], true)
}

const progressGetRequest = (state: any) => {
  return state.setIn(['pending', 'getProgress'], true)
}

const progressGetSuccess = (state: any, { payload: { data } }: any) => {
  const { proTrackLevels, isUserEverTakeProTrack } = data
  const currentProTrack = proTrackLevels.currentProTrack
  const onboardingLevels = new OnboardingLevels({
    current: currentProTrack
      ? new OnboardingProTrack({
          nextLevel: currentProTrack.nextLevel ? new OnboardingLevel(currentProTrack.nextLevel) : null,
          previousLevels: currentProTrack.previousLevels
            ? currentProTrack.previousLevels.reduce(
                (prevValue: any, level: any) => prevValue.push(new OnboardingLevel(level)),
                List()
              )
            : List(),
        })
      : new OnboardingProTrack(),
    new: proTrackLevels.newProTrackLevels
      ? List(proTrackLevels.newProTrackLevels.map((level: any) => new OnboardingLevel(level)))
      : List(),
    previous: proTrackLevels.previousProTracks
      ? List(
          proTrackLevels.previousProTracks.map(
            (proTrack: any) =>
              new OnboardingProTrack({
                nextLevel: proTrack.nextLevel ? new OnboardingLevel(proTrack.nextLevel) : null,
                previousLevels: proTrack.previousLevels
                  ? proTrack.previousLevels.reduce(
                      (previousValue: any, level: any) => previousValue.push(new OnboardingLevel(level)),
                      List()
                    )
                  : List(),
              })
          )
        )
      : List(),
    levels: proTrackLevels.levels
      ? List(proTrackLevels.levels.map((level: any) => new OnboardingLevel(level)))
      : List(),
  })

  const progress = new Progress({
    all: new ProgressAllInstance(data.progress.all),
    current: new ProgressCurrentInstance(data.progress.current),
  })

  const sprint = new Sprint({
    ...data.sprint,
    user: (data.sprint && data.sprint.user) || new SprintGoalRecord(),
    organization: (data.sprint && data.sprint.organization) || new SprintGoalRecord(),
  })

  const newIsUserEverTakeProTrack = state.get('isUserEverTakeProTrack') || isUserEverTakeProTrack

  if (data.progress && !data.progress.current) {
    const { stage } = data
    return state
      .setIn(['pending', 'getProgress'], false)
      .set('stage', stage)
      .set('canCompletePreviousActivity', data.canCompletePreviousActivity === true)
      .set('isCommitNotificationTimeHasCome', data.isCommitNotificationTimeHasCome)
      .set('isCommittedNotificationTimeHasCome', data.isCommittedNotificationTimeHasCome)
      .set('isUserCanSwitchProtrack', data.isUserCanSwitchProtrack)
      .set('organization', data.organization ? new IdentityOrganization(data.organization) : new IdentityOrganization())
      .set('onboardingLevels', onboardingLevels)
      .set(
        'progress',
        new Progress({
          all: new ProgressAllInstance(progress.all),
          current: new ProgressCurrentInstance(data.progress.current),
        })
      )
      .set('previousProTrackLevel', new ActiveProTrackLevel(data.previousProTrackLevel))
      .set('sprint', sprint)
      .set('isUserEverTakeProTrack', newIsUserEverTakeProTrack)
  }

  const parsedActions =
    data.actions &&
    data.actions.reduce((acc: any, action: any) => {
      return acc.set(action, action)
    }, Map())

  return state
    .setIn(['pending', 'getProgress'], false)
    .setIn(['pending', 'proTrackSubscription'], false)
    .set('day', data.progressDay)
    .set('organization', data.organization ? new IdentityOrganization(data.organization) : new IdentityOrganization())
    .set('activeLevel', data.proTrack ? new ActiveProTrackLevel(data.proTrack) : new ActiveProTrackLevel())
    .set('progress', progress)
    .set('canCompletePreviousActivity', data.canCompletePreviousActivity === true)
    .set('isCommitNotificationTimeHasCome', data.isCommitNotificationTimeHasCome)
    .set('isCommittedNotificationTimeHasCome', data.isCommittedNotificationTimeHasCome)
    .set('isUserCanSwitchProtrack', data.isUserCanSwitchProtrack)
    .set('lastActivity', new Activity(data.lastActivity))
    .set('actions', parsedActions)
    .set('stage', data.stage)
    .set('onboardingLevels', onboardingLevels)
    .set('sprint', sprint)
    .set('isUserEverTakeProTrack', newIsUserEverTakeProTrack)
}

const progressGetFail = (state: any) => {
  return state.setIn(['pending', 'getProgress'], false).setIn(['pending', 'proTrackSubscription'], false)
}

const progressCombinedGetRequest = (state: any) => {
  return state.setIn(['pending', 'getCombinedProgress'], true)
}

const progressCombinedGetFail = (state: any) => {
  return state.setIn(['pending', 'getCombinedProgress'], false).setIn(['pending', 'proTrackSubscription'], false)
}

const progressCombinedGetSuccess = (state: any, { payload: { data } }: any) => {
  const { currentProTrack } = data

  let levels = data.levels ? List(data.levels.map((level: any) => new OnboardingLevel(level))) : List()

  if (data.stage && data.stage === 'selection') {
    const proTrackLevels = data.proTrackLevels
    levels = proTrackLevels.levels
      ? List(proTrackLevels.levels.map((level: any) => new OnboardingLevel(level)))
      : List()
  }

  const onboardingLevels = new OnboardingLevels({
    current: currentProTrack
      ? new OnboardingProTrack({
          nextLevel: currentProTrack.nextLevel ? new OnboardingLevel(currentProTrack.nextLevel) : null,
          previousLevels: currentProTrack.previousLevels
            ? currentProTrack.previousLevels.reduce(
                (prevValue: any, level: any) => prevValue.push(new OnboardingLevel(level)),
                List()
              )
            : List(),
        })
      : new OnboardingProTrack(),
    new: data.newProTrackLevels ? List(data.newProTrackLevels.map((level: any) => new OnboardingLevel(level))) : List(),
    previous: data.previousProTracks
      ? List(
          data.previousProTracks.map(
            (proTrack: any) =>
              new OnboardingProTrack({
                nextLevel: proTrack.nextLevel ? new OnboardingLevel(proTrack.nextLevel) : null,
                previousLevels: proTrack.previousLevels
                  ? proTrack.previousLevels.reduce(
                      (previousValue: any, level: any) => previousValue.push(new OnboardingLevel(level)),
                      List()
                    )
                  : List(),
              })
          )
        )
      : List(),
    levels: levels,
  })

  return state
    .setIn(['pending', 'getCombinedProgress'], false)
    .setIn(['pending', 'proTrackSubscription'], false)
    .set('onboardingLevels', onboardingLevels)
}

const handleSubscribeToProTrack = (state: any) => {
  return state.setIn(['pending', 'proTrackSubscription'], true)
}

const saveTimePreferences = (state: any, { payload: { timePreferences } }: any) => {
  return state.merge(timePreferences.toJS())
}

const handleRequestSurveySuccess = (state: any, { payload }: any) => {
  const { surveyType } = payload
  if (surveyType === 'end') {
    state = state.setIn(['activeLevel', 'endSurveyRequested'], true)
  }
  return state
}

const handleMarkEndSurveyRequested = (state: any, { payload }: any) => {
  return state.setIn(['activeLevel', 'endSurveyRequested'], payload)
}

const activityHistoryGetRequest = (state: any) => {
  return state.setIn(['pending', 'getHistory'], true)
}

const activityHistoryGetSuccess = (state: any, { payload }: any) => {
  return state
    .setIn(
      ['activitiesHistory', 'items'],
      List(
        payload.data.map(
          (historyItem: any) =>
            new ActivitiesHistoryItem({
              activityId: historyItem.id,
              activityVideoId: historyItem.videoId,
              ...historyItem,
            })
        )
      )
    )
    .setIn(['activitiesHistory', 'pagination'], new ActivitiesHistoryPagination(payload.pagination))
    .setIn(['pending', 'getHistory'], false)
}

const handlePostAuthPhoneSuccess = (state: any) => state.set('isTokenSendToPhone', true)

const handlePostAuthEmailSuccess = (state: any) => state.set('isTokenSendToEmail', true)

const handleClearAuthStatuses = (state: any) => state.set('isTokenSendToEmail', false).set('isTokenSendToPhone', false)

const handleSetFeatureAsShown = (
  state: any,
  {
    payload: {
      request: { data },
    },
  }: any
) => {
  return state.setIn(['isShowFeatureInfo', Object.keys(data)[0]], false)
}
const handleSetFeatureTooltipAsShown = (state: any, { payload }: any) => {
  return state.setIn(['isShowFeatureInfo', payload], false)
}

const handleMarkLoginFlowAsFinished = (state: any) => state.set('isLoginFlowFinished', true)

const handleSetYesterdayStory = (state: any, { payload }: any) => {
  const activityIndex = state
    .getIn(['activitiesHistory', 'items'])
    .findIndex((activity: any) => activity.userActivityId === payload.data.userActivityId)

  return state
    .setIn(['lastActivity', 'hasStory'], true)
    .setIn(['activitiesHistory', 'items', activityIndex, 'hasStory'], true)
    .setIn(['onboardingChecklist', 'isStoryShared'], true)
}

const handleCustomStorySuccess = (state: any, { payload }: any) => {
  const activityIndex = state
    .getIn(['activitiesHistory', 'items'])
    .findIndex((activity: any) => activity.userActivityId === payload.data.userActivityId)
  const userLastActivityId = state.getIn(['lastActivity', 'userActivityId'])

  return state.getIn(['onboardingChecklist', 'isStoryShared'])
    ? userLastActivityId !== payload.data.userActivityId
      ? state.setIn(['activitiesHistory', 'items', activityIndex, 'hasStory'], true)
      : state
          .setIn(['activitiesHistory', 'items', activityIndex, 'hasStory'], true)
          .setIn(['lastActivity', 'hasStory'], true)
    : userLastActivityId !== payload.data.userActivityId
    ? state
        .setIn(['activitiesHistory', 'items', activityIndex, 'hasStory'], true)
        .setIn(['onboardingChecklist', 'isStoryShared'], true)
    : state
        .setIn(['activitiesHistory', 'items', activityIndex, 'hasStory'], true)
        .setIn(['lastActivity', 'hasStory'], true)
        .setIn(['onboardingChecklist', 'isStoryShared'], true)
}

const handleSetFirstLogin = (state: any, { payload }: any) => {
  return state.set('firstLogin', payload)
}

const handleUpdateOnboardingChecklist = (state: any, { payload }: any) => {
  const { propertyName, propertyValue } = payload
  return state.setIn(['onboardingChecklist', propertyName], propertyValue)
}

const handleSetIsUserEverTakeProTrack = (state: any, { payload }: any) => {
  return state.set('isUserEverTakeProTrack', payload)
}

const handleUpdateActivitySuccess = (state: any, { payload: { data } }: any) => {
  const index = state
    .getIn(['activitiesHistory', 'items'])
    .findIndex((el: Record<IActivitiesHistoryItem>) => el.get('userActivityId') === data.userActivityId)
  const activity = state.getIn(['activitiesHistory', 'items', index])
  let updatedActivity
  // activity doesn't exist in activitiesHistory if we make update from tour
  if (activity) {
    updatedActivity = activity.set('actionType', DID)
    return state
      .setIn(['activitiesHistory', 'items', index], updatedActivity)
      .setIn(['activitiesHistory', 'pending'], false)
  }
  return state.setIn(['activitiesHistory', 'pending'], false)
}

const handleUpdateActivity = (state: any) => {
  return state.setIn(['activitiesHistory', 'pending'], true)
}

const stopProTrackAction = (state: any, payload: any) => {
  const newState = progressGetSuccess(state, payload)
  return newState.setIn(['activitiesHistory', 'items'], List([]))
}

const increaseStoryReaction = (state: any) => {
  const storiesReactionsCount = state.getIn(['progress', 'current', 'storiesReactions'])
  return state.setIn(['progress', 'current', 'storiesReactions'], storiesReactionsCount + 1)
}

const deleteStory = (state: any, { payload }) => {
  // cleaning prev activity stories if removed from feed/my-stories
  const items = state.getIn(['activitiesHistory', 'items'])

  const index = items.toJS().findIndex(el => {
    if (el.story) {
      return el.story.id === payload.storyId
    }
    return false
  })

  if (index !== -1) {
    const updatedItem = state.getIn(['activitiesHistory', 'items', index]).set('story', null).set('hasStory', false)
    return state.setIn(['activitiesHistory', 'items', index], updatedItem)
  }

  return state
}

export default handleActions(
  {
    [REHYDRATE]: handleRehydrate,
    [FLUSH]: handleFlush,
    [authLogin.success as any]: handleAuthLoginSuccess,
    [changeIdentityRole as any]: handleChangeIdentityRole,
    [getProfile as any]: getProfileRequest,
    [combineActions(setCustomStory.success, setStory.success) as any]: handleCustomStorySuccess,
    [combineActions(getProfile.success, saveProfile.success, setOneSignalId.success) as any]: getProfileSuccess,
    [getProfile.fail as any]: getProfileFail,
    [getIdentityProgress as any]: progressGetRequest,
    [getIdentityCombinedProgress as any]: progressCombinedGetRequest,
    [subscribeToProTrack as any]: handleSubscribeToProTrack,
    [setAuthEmailStatus as any]: handleSetAuthEmailStatus,
    [getActivitiesHistory as any]: activityHistoryGetRequest,
    [getActivitiesHistory.success as any]: activityHistoryGetSuccess,
    [combineActions(
      getIdentityProgress.success as any,
      activityActions.commit.success,
      activityActions.did.success,
      activityActions.didnot.success,
      activityActions.complete.success,
      subscribeToProTrack.success,
      // unsubscribeProgress.success,
      sendYesterdayActivity.success,
      updateSubscribeToProTrack.success,
      silentSubscribeToProTrack.success
    ) as any]: progressGetSuccess,
    [getIdentityCombinedProgress.success as any]: progressCombinedGetSuccess,
    [combineActions(
      getIdentityProgress.fail,
      activityActions.commit.fail,
      activityActions.didnot.fail,
      activityActions.did.fail,
      activityActions.complete.fail,
      subscribeToProTrack.fail,
      sendYesterdayActivity.fail
    ) as any]: progressGetFail,
    [getIdentityCombinedProgress.fail as any]: progressCombinedGetFail,
    [activityActions.commit.success as any]: activityActionsCommitSuccess,
    [combineActions(
      activityActions.did.success,
      sendYesterdayActivity.success,
      sendYesterdayActivity.success,
      updateActivity.success
    ) as any]: activityActionDidSuccess,
    [types.IDENTITY_LOGOUT_SUCCESS]: logoutSuccess,
    [types.IDENTITY_FORCE_LOGOUT_SUCCESS]: logoutSuccess,
    [identity.saveTimePreferences]: saveTimePreferences,
    [requestSurvey.success as any]: handleRequestSurveySuccess,
    [postAuthPhone.success as any]: handlePostAuthPhoneSuccess,
    [postAuthEmail.success as any]: handlePostAuthEmailSuccess,
    [clearAuthStatuses as any]: handleClearAuthStatuses,
    [setFeatureAsShown as any]: handleSetFeatureAsShown,
    [setFeatureTooltipAsShown as any]: handleSetFeatureTooltipAsShown,
    [markLoginFlowAsFinished as any]: handleMarkLoginFlowAsFinished,
    [setYesterdayStory.success as any]: handleSetYesterdayStory,
    [setFirstLogin as any]: handleSetFirstLogin,
    [markEndSurveyRequested as any]: handleMarkEndSurveyRequested,
    [updateOnboardingChecklist as any]: handleUpdateOnboardingChecklist,
    [setIsUserEverTakeProTrack as any]: handleSetIsUserEverTakeProTrack,
    [updateActivity.success as any]: handleUpdateActivitySuccess,
    [updateActivity as any]: handleUpdateActivity,
    [unsubscribeProgress.success as any]: stopProTrackAction,
    [FEED_SET_REACTION]: increaseStoryReaction,
    [deleteStoryApiRequest.success as any]: deleteStory,
  },
  new Identity()
)
